<template>
  <Dialog
    :title="
      type === 'add' ? '新增教师' : type === 'detail' ? '教师详情' : '修改教师'
    "
    :visible.sync="dialogVisible"
    width="640px"
    :close-on-click-modal="false"
    :before-close="cancel"
  >
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-width="100px"
      :disabled="isDetail"
    >
      <el-form-item label="教师姓名" prop="teacherName">
        <el-input
          v-model.trim="form.teacherName"
          maxlength="50"
          placeholder="请输入教师姓名"
        ></el-input>
      </el-form-item>

      <el-form-item label="登录账号" prop="account">
        <el-input
          v-model.trim="form.account"
          maxlength="10"
          placeholder="大小写字母/中文/数字/下划线，最多10位"
          :disabled="type === 'edit'"
          @input="onAccountInput"
        ></el-input>
      </el-form-item>
      <el-form-item label="教师类型" prop="teacherType">
        <el-select
          v-model="form.teacherType"
          placeholder="请选择"
          style="width: 100%"
        >
          <el-option label="负责人" value="1"></el-option>
          <el-option label="讲师" value="2"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="头衔" prop="teacherTitle">
        <el-input
          v-model.trim="form.teacherTitle"
          maxlength="50"
          show-word-limit
          placeholder="请输入一句话头衔"
        ></el-input>
      </el-form-item>
      <el-form-item label="头像" prop="avatar">
        <UploadCropImage
          v-model="form.avatar"
          aspect-ratio="1:1"
          :output-width="400"
          :max-size="5"
          :disabled="isDetail"
          tip="建议比例 1:1，上传后按正方形裁剪再提交"
          @input="$refs.formRef && $refs.formRef.validateField('avatar')"
          @remove="$refs.formRef && $refs.formRef.validateField('avatar')"
        />
      </el-form-item>
      <el-form-item label="手机号" prop="mobile">
        <el-input
          v-model.trim="form.mobile"
          maxlength="20"
          placeholder="请输入手机号"
        ></el-input>
      </el-form-item>
      <el-form-item label="邮箱" prop="email">
        <el-input
          v-model.trim="form.email"
          maxlength="100"
          placeholder="请输入邮箱"
        ></el-input>
      </el-form-item>
      <el-form-item label="简介" prop="teacherInfo">
        <el-input
          type="textarea"
          v-model.trim="form.teacherInfo"
          :rows="4"
          maxlength="500"
          show-word-limit
          placeholder="请输入简介"
        ></el-input>
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-radio-group v-model="form.status">
          <el-radio label="0">正常</el-radio>
          <el-radio label="1">停用</el-radio>
        </el-radio-group>
      </el-form-item>
    </el-form>
    <div slot="footer">
      <el-button plain round size="small" @click="cancel">
        {{ isDetail ? "关 闭" : "取 消" }}
      </el-button>
      <el-button
        v-if="!isDetail"
        plain
        round
        size="small"
        type="primary"
        :loading="submitLoading"
        @click="submitForm"
        >确 定</el-button
      >
    </div>
  </Dialog>
</template>

<script>
import UploadCropImage from "@/components/UploadCropImage";
import { addTeacher, updateTeacher, getTeacherDetail } from "@/api/teacher";
import {
  validatePhoneOptional,
  validateEmailOptional,
  validateTeacherAccount
} from "@/utils/validate";

const defaultForm = () => ({
  teacherId: undefined,
  teacherName: "",
  teacherCode: "",
  account: "",
  teacherType: "2",
  teacherTitle: "",
  avatar: "",
  mobile: "",
  email: "",
  teacherInfo: "",
  status: "0"
});

export default {
  name: "TeacherForm",
  components: { UploadCropImage },
  data() {
    return {
      dialogVisible: false,
      submitLoading: false,
      type: "add",
      form: defaultForm(),
      rules: {
        teacherName: [
          { required: true, message: "请输入教师姓名", trigger: "blur" }
        ],
        account: [
          { required: true, validator: validateTeacherAccount, trigger: "blur" }
        ],
        teacherType: [
          { required: true, message: "请选择教师类型", trigger: "change" }
        ],
        teacherTitle: [
          { required: true, message: "请输入头衔", trigger: "blur" },
          { max: 50, message: "头衔不能超过50个字符", trigger: "blur" }
        ],
        avatar: [{ required: true, message: "请上传头像", trigger: "change" }],
        mobile: [{ validator: validatePhoneOptional, trigger: "blur" }],
        email: [{ validator: validateEmailOptional, trigger: "blur" }],
        teacherInfo: [
          { required: true, message: "请输入简介", trigger: "blur" }
        ],
        status: [{ required: true, message: "请选择状态", trigger: "change" }]
      }
    };
  },
  computed: {
    isDetail() {
      return this.type === "detail";
    }
  },
  methods: {
    onAccountInput(val) {
      const next = String(val || "")
        .replace(/[^\u4e00-\u9fa5A-Za-z0-9_]/g, "")
        .slice(0, 10);
      if (next !== this.form.account) {
        this.form.account = next;
      }
    },
    async open(type, row = {}) {
      this.type = type;
      this.dialogVisible = true;
      if ((type === "edit" || type === "detail") && row.teacherId != null) {
        try {
          const result = await getTeacherDetail(row.teacherId);
          const data = (result && result.data) || row;
          this.form = {
            teacherId: data.teacherId,
            teacherName: data.teacherName || "",
            teacherCode: data.teacherCode || "",
            account: data.account || "",
            teacherType:
              data.teacherType != null ? String(data.teacherType) : "2",
            teacherTitle: data.teacherTitle || "",
            avatar: data.avatar || "",
            mobile: data.mobile || "",
            email: data.email || "",
            teacherInfo: data.teacherInfo || "",
            status: data.status != null ? String(data.status) : "0"
          };
        } catch (e) {
          this.cancel();
          return;
        }
      } else {
        this.form = defaultForm();
      }
      this.$nextTick(() => {
        this.$refs.formRef && this.$refs.formRef.clearValidate();
      });
    },
    async submitForm() {
      if (this.submitLoading || this.isDetail) return;
      try {
        await this.$refs.formRef.validate();
      } catch (e) {
        return;
      }
      this.submitLoading = true;
      const request = this.form.teacherId ? updateTeacher : addTeacher;
      try {
        await request(this.form);
        this.$message.success(this.form.teacherId ? "修改成功" : "新增成功");
        this.cancel();
        this.$emit("success");
      } catch (e) {
      } finally {
        this.submitLoading = false;
      }
    },
    cancel() {
      this.dialogVisible = false;
      this.form = defaultForm();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/views/Teacher/TeacherForm.vue